/**
 * NeedsYourAttention (revised) — per-row warm amber backgrounds with an
 * explicit action button inside each capsule.
 *
 * Palette (per user spec, Figma-authoritative):
 *   Row 1: #FFDF80
 *   Row 2: #FFE599
 *   Row 3: #FFECB3
 * Additional rows cycle back to the deepest shade.
 *
 * Height parity: the outer <Card> uses `h-full` and its <CardContent> is a
 * flex column so this section matches the height of the sibling Committed
 * Money card in the same row.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowRight, Bell, RefreshCw, Upload, Tag, Repeat, ShieldCheck } from 'lucide-react';

const KIND_ICON = {
  payment_due:          Bell,
  reconciliation:       RefreshCw,
  upload_reminder:      Upload,
  category_remap:       Tag,
  recurring_suggestion: Repeat,
};

const KIND_LABEL = {
  payment_due:          'UPCOMING PAYMENT',
  reconciliation:       'REVIEW DUPLICATES',
  upload_reminder:      'IMPORT NEEDED',
  category_remap:       'CATEGORY CHANGE',
  recurring_suggestion: 'RECURRING PATTERN',
};

/** Per-row CTA label — declarative so all copy lives in one place. */
const KIND_CTA = {
  payment_due:          'Review upcoming payment',
  reconciliation:       'Review duplicates',
  upload_reminder:      'Import transactions',
  category_remap:       'Review categories',
  recurring_suggestion: 'Open recurring',
};

const AMBER_SHADES = ['#FFDF80', '#FFE599', '#FFECB3'];

export default function NeedsYourAttention({
  items = [],
  loading = false,
  onOpenDrawer,
  onNavigate,
}) {
  if (loading) {
    return (
      <Card data-testid="dashboard-needs-attention" className="border-border/70 h-full">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Needs Your Attention</CardTitle></CardHeader>
        <CardContent className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></CardContent>
      </Card>
    );
  }

  if (!items.length) {
    return (
      <Card data-testid="dashboard-needs-attention" className="border-border/70 h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Needs Your Attention</CardTitle>
          <p className="text-xs text-muted-foreground">Financial changes and upcoming commitments to review.</p>
        </CardHeader>
        <CardContent className="pt-2 flex-1 flex items-center">
          <div className="flex items-center gap-3 py-4 text-muted-foreground" data-testid="attention-empty">
            <ShieldCheck className="h-4 w-4 text-emerald" />
            <p className="text-sm">Nothing to review right now &mdash; you&apos;re all caught up.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="dashboard-needs-attention" className="border-border/70 h-full flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-gold" /> Needs Your Attention
        </CardTitle>
        <p className="text-xs text-muted-foreground">Financial changes and upcoming commitments to review.</p>
      </CardHeader>
      <CardContent className="pt-1 space-y-2 flex-1 flex flex-col">
        {/* Phase 84.22.13 — cap raised from 4 → 5 so a user with a
            full stack of upcoming commitments (rent + 2 SIPs + home
            loan EMI + car loan EMI) sees all of them without a hidden
            "wait, where's my car loan?" moment. A "See all →" footer
            link appears when there are more than 5 items so the
            section stays honest about truncation. */}
        {items.slice(0, 5).map((item, idx) => {
          const Icon    = KIND_ICON[item.kind] || Bell;
          const bg      = AMBER_SHADES[idx % AMBER_SHADES.length];
          const label   = KIND_LABEL[item.kind] || (item.kind || '').replace('_', ' ').toUpperCase();
          const ctaText = item.cta?.label || KIND_CTA[item.kind] || 'Open';
          const handle = () => {
            if (item.drawer_type) onOpenDrawer?.(item.drawer_type, item.linked_id || item.id);
            else if (item.cta?.target) onNavigate?.(item.cta.target);
          };
          return (
            <div
              key={item.id}
              style={{ backgroundColor: bg }}
              className="rounded-xl px-4 py-3.5 text-charcoal"
              data-testid={`attention-row-${item.kind}`}
            >
              <div className="flex items-start gap-3">
                <span className="h-8 w-8 rounded-lg grid place-items-center shrink-0 bg-white/50 text-charcoal">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-medium text-charcoal/70">{label}</div>
                  <div className="text-sm font-semibold text-charcoal truncate">{item.title}</div>
                  {item.sub_line && <div className="text-xs text-charcoal/70 truncate mt-0.5">{item.sub_line}</div>}
                </div>
              </div>
              <div className="mt-2.5 flex justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handle}
                  className="h-auto px-0 text-charcoal hover:bg-transparent hover:text-charcoal/80 font-medium text-xs"
                  data-testid={`attention-cta-${item.kind}`}
                >
                  {ctaText}
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
            </div>
          );
        })}
        {items.length > 5 && (
          <div className="pt-1 text-right">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onNavigate?.('/recurring')}
              className="h-auto px-0 text-xs font-medium text-muted-foreground hover:text-charcoal"
              data-testid="attention-see-all"
            >
              See all {items.length} →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
